// src/components/queue/QueueToRussiaPage.jsx
import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock, AlertCircle, ExternalLink, Globe, Search } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const QueueToRussiaPage = () => {
  const { t } = useLanguage();
  const [queueData, setQueueData] = useState({
    page_title: '',
    page_content: '',
    structured_data: {},
    raw_html: '',
    last_updated: null,
    status: 'loading',
    source: '',
    error: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); // Add search state

  // Function to fetch queue data from backend
  const fetchQueueData = async () => {

    setIsLoading(true);
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/queue-russia/`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      
      setQueueData({
        ...data,
        error: null
      });
    } catch (error) {
      console.error('Failed to fetch queue data:', error);
      setQueueData(prev => ({
        ...prev,
        error: error.message,
        status: 'error'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Force refresh function
  const forceRefresh = () => {
    fetchQueueData();
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchQueueData();
    
    // Auto-refresh every 3 minutes
    const interval = setInterval(fetchQueueData, 180000);
    
    return () => clearInterval(interval);
  }, []);

  // Format the last updated time
  const formatLastUpdated = () => {
    if (!queueData.last_updated) return t('notAvailable');
    
    const date = new Date(queueData.last_updated);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Get queue info
  const queueInfo = queueData.structured_data?.queue_info || {};
  const title = queueInfo.title || '';
  const time = queueInfo.time || '';

  // Count total and green rows
  const getTotalAndGreenCount = () => {
    if (!queueData.structured_data?.tables?.[0]) {
      return { total: 0, green: 0 };
    }
    
    const table = queueData.structured_data.tables[0];
    const total = table.rows?.length || 0;
    const green = table.green_rows?.filter(Boolean).length || 0;
    
    return { total, green };
  };

  const { total, green } = getTotalAndGreenCount();

  // Filter table rows based on search term
  const getFilteredTableData = () => {
    if (!queueData.structured_data?.tables?.[0]) {
      return null;
    }

    const table = queueData.structured_data.tables[0];
    
    if (!searchTerm.trim()) {
      return table;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    
    const filteredRows = table.rows.filter((row, index) => {
      // Search through all cells in the row
      return row.some(cell => 
        String(cell).toLowerCase().includes(searchLower)
      );
    });

    // Filter green_rows to match the filtered rows
    const filteredGreenRows = table.green_rows ? 
      table.rows.map((row, index) => table.green_rows[index])
               .filter((_, index) => 
                 table.rows[index].some(cell => 
                   String(cell).toLowerCase().includes(searchLower)
                 )
               ) : [];

    return {
      ...table,
      rows: filteredRows,
      green_rows: filteredGreenRows
    };
  };

  const filteredTable = getFilteredTableData();
  const displayTable = filteredTable || queueData.structured_data?.tables?.[0];

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
              {t('queueToRussia')}
            </h1>
            <p className="text-gray-600">
              {t('queueToRussiaDescription')}
            </p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <button
              onClick={forceRefresh}
              disabled={isLoading}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isLoading 
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? t('loading') : t('refresh')}
            </button>
          </div>
        </div>
        
        {/* Last Updated Info */}
        <div className="mt-4 text-sm text-gray-600 text-right">
          {t('lastUpdated')}: {formatLastUpdated()}
        </div>
      </div>

      {/* Error Display */}
      {queueData.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 font-medium">{t('error')}: {queueData.error}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-6">
        {isLoading && !queueData.page_content ? (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mr-3" />
              <span className="text-lg text-gray-600">{t('loadingQueueData')}</span>
            </div>
          </div>
        ) : (
          <>
            {/* Title and Time Display */}
            {(title || time) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                {title && (
                  <h2 className="text-xl font-semibold text-blue-900 mb-2 whitespace-pre-line">
                    {title}
                  </h2>
                )}
                {time && (
                  <p className="text-blue-700 font-medium">
                    {time}
                  </p>
                )}
              </div>
            )}

            {/* Statistics */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Всего в очереди</div>
                  <div className="text-3xl font-bold text-gray-900">{total}</div>
                </div>
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="text-sm text-green-700 mb-1">Зелёная зона</div>
                  <div className="text-3xl font-bold text-green-900">{green}</div>
                </div>
              </div>
            </div>

            {/* Search Field */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Поиск по номеру, марке, стране..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <span className="text-gray-400 hover:text-gray-600">×</span>
                  </button>
                )}
              </div>
              {searchTerm && displayTable && (
                <div className="mt-2 text-sm text-gray-600">
                  Найдено: {displayTable.rows.length} из {queueData.structured_data?.tables?.[0]?.rows?.length || 0}
                </div>
              )}
            </div>

            {/* Structured Data Display */}
            {queueData.structured_data && displayTable && (
              <div className="space-y-6">
                {/* Tables */}
                {displayTable.rows && displayTable.rows.length > 0 ? (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b">
                      <h2 className="text-lg font-semibold text-gray-800">
                        {t('queueInformation')}
                      </h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        {displayTable.headers && displayTable.headers.length > 0 && (
                          <thead className="bg-gray-50">
                            <tr>
                              {displayTable.headers.map((header, headerIndex) => (
                                <th
                                  key={headerIndex}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayTable.rows.map((row, rowIndex) => {
                            // Use the green_rows array to determine if row should be green
                            const isRowGreen = displayTable.green_rows && displayTable.green_rows[rowIndex];
                            const rowClass = isRowGreen 
                              ? 'bg-green-100' 
                              : (rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50');
                            
                            // Highlight search term in cells
                            const highlightText = (text) => {
                              if (!searchTerm.trim()) return text;
                              
                              const searchLower = searchTerm.toLowerCase();
                              const textStr = String(text);
                              const index = textStr.toLowerCase().indexOf(searchLower);
                              
                              if (index === -1) return text;
                              
                              const before = textStr.substring(0, index);
                              const match = textStr.substring(index, index + searchTerm.length);
                              const after = textStr.substring(index + searchTerm.length);
                              
                              return (
                                <>
                                  {before}
                                  <mark className="bg-yellow-200">{match}</mark>
                                  {after}
                                </>
                              );
                            };
                            
                            return (
                              <tr key={rowIndex} className={rowClass}>
                                {row.map((cell, cellIndex) => (
                                  <td
                                    key={cellIndex}
                                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                  >
                                    {searchTerm ? highlightText(cell) : cell}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <p className="text-gray-500">Ничего не найдено по запросу "{searchTerm}"</p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="mt-2 text-blue-600 hover:text-blue-800"
                    >
                      Очистить поиск
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>
          {t('dataSource')}: {queueData.source}
        </p>
        <p className="mt-1">
          {t('pageAutoRefreshes')}
        </p>
      </div>
    </div>
  );
};

export default QueueToRussiaPage;